import { useMutation } from '@tanstack/react-query';
import { Button, Notice, Section, TextField } from '@wodi/ui';
import { useState } from 'react';

import { changePassword } from '../api/account-security.ts';
import { ApiError } from '../api/client.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen } from '../layout/screen.tsx';
import { toasts } from '../toasts.ts';

// Account > Password (ADR 0037): the current password, then the new one twice. Anyone else
// signed in as this person is signed out; this session carries on.

export function PasswordSection() {
  const online = useOnline();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const change = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setAgain('');
      setChanged(true);
      toasts.show({ title: 'Password changed' });
    },
    onError: () => {
      setCurrent('');
    },
  });

  return (
    <Section
      title="Password"
      description="Use at least 12 characters: a few unrelated words work well. Common passwords are refused."
    >
      <form
        className={screen.page}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const mismatch =
            current === ''
              ? 'Enter your current password.'
              : next === ''
                ? 'Enter a new password.'
                : next !== again
                  ? 'The two new passwords are different. Type them again.'
                  : null;
          setProblem(mismatch);
          setChanged(false);
          if (mismatch === null) change.mutate();
        }}
      >
        {online ? null : (
          <Notice tone="info">
            You&apos;re offline. Your password can be changed once you reconnect.
          </Notice>
        )}
        {changed ? (
          <Notice tone="info">
            Password changed. Anyone signed in as you on another computer was signed out.
          </Notice>
        ) : null}
        {change.error === null ? null : (
          <Notice tone="error">
            {change.error instanceof ApiError
              ? change.error.message
              : "Your password wasn't changed. Try again."}
          </Notice>
        )}
        {problem === null ? null : <Notice tone="error">{problem}</Notice>}
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          width="md"
          value={current}
          onChange={setCurrent}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          width="md"
          value={next}
          onChange={setNext}
        />
        <TextField
          label="Type the new password again"
          type="password"
          autoComplete="new-password"
          width="md"
          value={again}
          onChange={setAgain}
        />
        <div>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={change.isPending}
            pendingLabel="Changing…"
          >
            Change password
          </Button>
        </div>
      </form>
    </Section>
  );
}
